<template>
  <v-container fluid class="profile-container">
    <v-card v-if="userReady" class="profile-card">
      <v-card-title
        :class="['profile-card-header', employeeHeaderClass, 'text-white', 'text-h5', 'text-center', 'font-weight-bold']">
        Edit Profile
      </v-card-title>
      <v-divider></v-divider>
      <v-form @submit.prevent="saveProfile" class="pa-5">
        <v-row>
          <v-col cols="12" md="6">
            <!-- Non-editable fields -->
            <v-text-field label="Username" v-model="user.username" disabled />
            <v-text-field label="Bar" v-model="user.bar" disabled />
            <v-text-field label="Role" :model-value="displayRole" disabled />
            <v-text-field label="Birthday" v-model="tempBirthday" disabled />
            <!-- Editable fields -->
            <v-text-field
              v-model="tempEmail"
              label="Email"
              required
              :error="!!errors.email"
              :error-messages="errors.email"
              @input="errors.email = null"
            />
            <!-- Phone: Editable dropdown and local number side-by-side -->
            <v-row dense>
              <v-col cols="4">
                <v-select :items="europeCodes" v-model="tempPhoneCountry" label="Country Code" item-title="label"
                  item-value="value" hide-details>
                  <template #selection="data">
                    <span>{{ data.item.value }}</span>
                  </template>
                </v-select>
              </v-col>
              <v-col cols="8">
                <v-text-field v-model="tempPhoneLocal" label="Local Phone Number" required></v-text-field>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12" md="6" class="text-center">
            <v-avatar size="140">
              <img :key="previewImageSrc" :src="previewImageSrc" alt="Profile Picture" class="profile-img" :style="profileImgStyle"
                @error="useDefaultProfilePicture" />
            </v-avatar>
            <div class="mt-4">
              <v-btn color="primary" @click="triggerFileSelect">Choose File</v-btn>
              <div class="text-caption grey--text mt-2">Max file size: 10 MB.</div>
              <input ref="fileInput" type="file" accept="image/*" class="hidden-file-input" @change="onFileChange" />
            </div>
          </v-col>
        </v-row>
        <v-divider class="my-5"></v-divider>
        <v-row justify="center">
          <v-col cols="auto">
            <v-btn
              color="success"
              type="submit"
              :loading="isSaving"
              :disabled="isSaving"
            >
              Save
            </v-btn>
          </v-col>
          <v-col cols="auto">
            <v-btn color="error" outlined @click="cancelEdit">Cancel</v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-card>
    <v-skeleton-loader
      v-else
      type="card"
      class="mx-auto"
      style="max-width:620px; height:400px;"
    />
  </v-container>
</template>

<script>
import axios from "axios";

export default {
  name: "ProfileEdit",
  data() {
    return {
      user: null,
      userReady: false,
      tempEmail: "",
      // Phone split: country code and local part
      tempPhoneCountry: "",
      tempPhoneLocal: "",
      tempBirthday: "",
      tempProfilePicture: null,
      previewUrl: null,
      defaultProfilePicture: "/media/profile_pictures/default_profile.png",
      saveError: '',
      errors: {},
      isSaving: false,
      europeCodes: [
        { value: "+30", label: "+30 Greece" },
        { value: "+31", label: "+31 Netherlands" },
        { value: "+32", label: "+32 Belgium" },
        { value: "+33", label: "+33 France" },
        { value: "+34", label: "+34 Spain" },
        { value: "+36", label: "+36 Hungary" },
        { value: "+39", label: "+39 Italy" },
        { value: "+40", label: "+40 Romania" },
        { value: "+41", label: "+41 Switzerland" },
        { value: "+43", label: "+43 Austria" },
        { value: "+44", label: "+44 United Kingdom" },
        { value: "+45", label: "+45 Denmark" },
        { value: "+46", label: "+46 Sweden" },
        { value: "+47", label: "+47 Norway" },
        { value: "+48", label: "+48 Poland" },
        { value: "+49", label: "+49 Germany" },
        { value: "+351", label: "+351 Portugal" },
        { value: "+353", label: "+353 Ireland" },
        { value: "+354", label: "+354 Iceland" },
        { value: "+355", label: "+355 Albania" },
        { value: "+356", label: "+356 Malta" },
        { value: "+357", label: "+357 Cyprus" },
        { value: "+358", label: "+358 Finland" },
        { value: "+370", label: "+370 Lithuania" },
        { value: "+371", label: "+371 Latvia" },
        { value: "+372", label: "+372 Estonia" },
        { value: "+386", label: "+386 Slovenia" },
      ],
    };
  },
  computed: {
    currentProfileImage() {
      return this.user && this.user.profile_picture_url
        ? this.user.profile_picture_url
        : this.defaultProfilePicture;
    },
    previewImageSrc() {
      return this.previewUrl || this.currentProfileImage;
    },
    employeeHeaderClass() {
      if (!this.user || !this.user.bar) return "";
      if (this.user.bar === "Temple Bar") return "header-temple";
      else if (this.user.bar === "Stefanshus") return "header-stefanshus";
      else if (this.user.bar === "Klovnen") return "header-klovnen";
      else if (this.user.bar === "Understellet") return "header-understellet";
      else if (this.user.bar === "Management") return "header-management";
      return "";
    },
    profileImgStyle() {
      if (this.user && this.user.is_vip) {
        return { border: "3px solid #ffd700" };
      } else if (this.user && this.user.bar) {
        if (this.user.bar === "Temple Bar") return { border: "3px solid #541B21" };
        else if (this.user.bar === "Stefanshus") return { border: "3px solid #156273" };
        else if (this.user.bar === "Klovnen") return { border: "3px solid #E47C25" };
        else if (this.user.bar === "Understellet") return { border: "3px solid #00A287" };
        else if (this.user.bar === "Management") return { border: "3px solid #0d0d0d" };
        return { border: "3px solid #1976d2" };
      }
      return {};
    },
    cooldownActive() {
      if (!this.user || !this.user.last_profile_picture_update) return false;
      const lastUpdate = new Date(this.user.last_profile_picture_update);
      const now = new Date();
      const cooldownPeriod = 7 * 24 * 60 * 60 * 1000;
      return now.getTime() < lastUpdate.getTime() + cooldownPeriod;
    },
    nextUploadAllowed() {
      if (!this.user || !this.user.last_profile_picture_update) return null;
      const lastUpdate = new Date(this.user.last_profile_picture_update);
      return new Date(lastUpdate.getTime() + 7 * 24 * 60 * 60 * 1000);
    },
    formattedNextUploadAllowed() {
      return this.nextUploadAllowed ? this.nextUploadAllowed.toLocaleString() : "";
    },
    displayRole() {
      if (!this.user) return '';
      return this.user.role === 'supermanager'
        ? 'Management'
        : (this.user.role_pretty || '');
    },
  },
  methods: {
    // Fetches the full user object for display in non-editable fields
    async fetchFullUser() {
      try {
        const resp = await axios.get("/api/user/", { withCredentials: true });
        if (resp.data && !resp.data.error) {
          // Only assign user fields relevant for display
          this.user = {
            username: resp.data.username,
            bar: resp.data.bar,
            role: resp.data.role,
            role_pretty: resp.data.role_pretty,
            birthday: resp.data.birthday,
            profile_picture_url: resp.data.profile_picture_url,
            is_vip: resp.data.is_vip,
            last_profile_picture_update: resp.data.last_profile_picture_update,
          };
          this.tempBirthday = this.user.birthday ? this.formatBirthday(this.user.birthday) : "N/A";
        } else {
          this.user = null;
        }
      } catch (err) {
        console.error("Error fetching user (full):", err);
        this.user = null;
      }
    },
    // Fetches only the editable profile data for the form
    async fetchEditData() {
      try {
        const endpoint = this.user.is_vip
          ? "/api/vip-profile/"
          : "/api/profile/edit/";
        const resp = await axios.get(endpoint, { withCredentials: true });
        if (resp.data && !resp.data.error) {
          this.tempEmail = resp.data.user?.email || "";
          // Split the stored phone_number into country code and local part
          const rawPhone = this.user.is_vip ? resp.data.phone : resp.data.phone_number;
          if (rawPhone) {
            let found = false;
            for (const code of this.europeCodes) {
              if (rawPhone.startsWith(code.value)) {
                this.tempPhoneCountry = code.value;
                this.tempPhoneLocal = rawPhone.substring(code.value.length).trim();
                found = true;
                break;
              }
            }
            if (!found) {
              this.tempPhoneCountry = "";
              this.tempPhoneLocal = rawPhone;
            }
          } else {
            this.tempPhoneCountry = "";
            this.tempPhoneLocal = "";
          }
          // If the backend provides last_profile_picture_update, update user object
          if (resp.data.last_profile_picture_update && this.user) {
            this.user.last_profile_picture_update = resp.data.last_profile_picture_update;
          }
        } else {
          this.tempEmail = "";
          this.tempPhoneCountry = "";
          this.tempPhoneLocal = "";
        }
      } catch (err) {
        console.error("Error fetching editable profile data:", err);
        this.tempEmail = "";
        this.tempPhoneCountry = "";
        this.tempPhoneLocal = "";
      }
    },
    // Method to format birthday from "YYYY-MM-DD" to "DD-MM-YYYY"
    formatBirthday(dateStr) {
      const parts = dateStr.split("-");
      if (parts.length === 3) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
      return dateStr;
    },
    useDefaultProfilePicture(e) {
      e.target.src = this.defaultProfilePicture;
    },
    triggerFileSelect() {
      if (this.cooldownActive) {
        alert(
          `You can only change your profile picture once per week. You can change it again on: ${this.formattedNextUploadAllowed}`
        );
        return;
      }
      this.$refs.fileInput.click();
    },
    onFileChange(e) {
      const files = e.target.files;
      if (files && files.length > 0) {
        const file = files[0];
        // Enforce max file size of 10 MB for profile pictures
        const maxSize = 10 * 1024 * 1024;
        if (file.size > maxSize) {
          alert('Profile picture upload failed: file size exceeds 10 MB limit.');
          e.target.value = '';
          return;
        }
        if (this.cooldownActive) {
          e.target.value = "";
          alert(
            `You can only change your profile picture once per week. You can change it again on: ${this.formattedNextUploadAllowed}`
          );
          return;
        }
        if (this.previewUrl) {
          URL.revokeObjectURL(this.previewUrl);
          this.previewUrl = null;
        }
        this.tempProfilePicture = file;
        this.previewUrl = URL.createObjectURL(file);
      }
    },
    async saveProfile() {
      if (this.isSaving) return;
      this.isSaving = true;
      try {
        this.errors = {};
        this.saveError = '';
        const formData = new FormData();
        formData.append("email", this.tempEmail);
        const phoneField = this.user.is_vip ? "phone" : "phone_number";
        // Combine country code and local phone number into one string
        const combinedPhone = (this.tempPhoneCountry + " " + this.tempPhoneLocal).trim();
        formData.append(phoneField, combinedPhone);
        if (this.tempProfilePicture) {
          formData.append("profile_picture", this.tempProfilePicture);
        }
        const patchUrl = this.user.is_vip
          ? "/api/vip-profile/"
          : "/api/profile/edit/";
        await axios.patch(patchUrl, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true,
        });
        if (this.previewUrl) {
          URL.revokeObjectURL(this.previewUrl);
          this.previewUrl = null;
        }
        this.$router.push({ name: "ProfilePage" });
      } catch (error) {
        console.error("Error saving profile:", error);
        if (error.response && error.response.status === 400) {
          this.errors.email = [error.response.data.error || 'Email address already in use.'];
        } else if (error.response) {
          this.saveError = error.response.data.detail || 'Failed to save profile.';
        } else {
          this.saveError = 'Network error. Please try again.';
        }
      } finally {
        this.isSaving = false;
      }
    },
    cancelEdit() {
      this.$router.push({ name: "ProfilePage" });
    },
  },
  async created() {
    await this.fetchFullUser();
    await this.fetchEditData();
    this.userReady = true;
  },
  unmounted() {
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
    }
  },
};
</script>

<style scoped>
.profile-container {
  background: linear-gradient(to bottom, #fafafa, #e7effa, #d2e3f5);
  min-height: 100vh;
  padding: 30px 20px;
}
.profile-card {
  max-width: 620px;
  margin: 0 auto;
  border-radius: 12px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
}
.profile-card-header {
  font-size: 18px;
  font-weight: bold;
  padding: 10px;
  color: #fff;
}
.header-temple {
  background-color: #541B21;
}
.header-stefanshus {
  background-color: #156273;
}
.header-klovnen {
  background-color: #E47C25;
}
.header-understellet {
  background-color: #00A287;
}
.header-management {
  background-color: #0d0d0d;
}
.profile-img {
  width: 140px;
  height: 140px;
  object-fit: cover;
  border-radius: 50%;
}
.hidden-file-input {
  display: none;
}
</style>
